/**
 * Provisioning API (brief §4/§4H): device inventory, open-provisioning
 * windows, the adoption queue, and firmware images/rollouts.
 *
 * Windows grant bootstrap only; full configuration is released when a device
 * is adopted (ADR 0026). The active-window list drives the persistent §4H
 * banner shown across the ops app.
 */

import { apiFetch } from "./client";

/** A device in the fleet inventory. */
export interface DeviceRow {
  id: number;
  mac: string;
  vendor: string;
  model: string;
  label: string | null;
  /** Extensions on the device's lines, primary first (search by extension). */
  extensions: string[];
  adoption_state: "pre_registered" | "pending" | "adopted" | "rejected";
  provisioning_state: string;
  firmware_current: string | null;
  firmware_target_id: number | null;
  button_template_id: number | null;
  last_seen_ip: string | null;
  last_provisioned: string | null;
  /** Set when a served config changed the phone's subscriptions (key identities, identity hosts, server type) — a live fetch does not re-subscribe; cleared by `confirmDeviceRebooted` (ADR 0028 amendment 2026-10-19). */
  reboot_needed_since: string | null;
  reboot_reason: string | null;
}

/** An open-provisioning window (§4H). */
export interface ProvisioningWindowRow {
  id: number;
  opened_by: number;
  opened_at: string | null;
  expires_at: string | null;
  scope_kind: "mac_list" | "vendor_oui" | "source_cidr";
  scope_value: string[] | null;
  internet_allowed: boolean;
  reason: string | null;
  closed_at: string | null;
  request_count: number;
}

/** A device awaiting an adoption decision. */
export interface AdoptionPending {
  device_id: number;
  mac: string;
  vendor: string;
  model: string;
  adoption_state: string;
  last_seen_ip: string | null;
  pinned_cert_fingerprint: string | null;
}

/** A historical adoption-queue entry. */
export interface AdoptionHistory {
  id: number;
  mac: string;
  first_seen: string | null;
  source_ip: string | null;
  window_id: number | null;
  decision: string | null;
  decided_at: string | null;
}

/** A registered firmware image. */
export interface FirmwareImageRow {
  id: number;
  vendor: string;
  model: string;
  version: string;
  filename: string;
  sha256: string;
  release_notes: string | null;
}

/** A staged firmware rollout. */
export interface FirmwareRolloutRow {
  id: number;
  image_id: number;
  target_kind: string;
  target_devices: number[] | null;
  window_start: string | null;
  window_end: string | null;
  state: string;
}

export function getDevices(): Promise<{ items: DeviceRow[]; total: number }> {
  return apiFetch("/api/provisioning/devices");
}

/** URL of a device's rendered config (same-origin GET; provisioning.view). */
export function deviceConfigUrl(mac: string): string {
  return `/api/provisioning/devices/${mac}/config`;
}

export function getActiveWindows(): Promise<{
  items: ProvisioningWindowRow[];
  total: number;
}> {
  return apiFetch("/api/provisioning/windows");
}

export interface OpenWindowIn {
  minutes: number;
  scope_kind: string;
  scope_value: string[];
  internet_allowed?: boolean;
  reason?: string;
}

export function openProvisioningWindow(
  body: OpenWindowIn,
): Promise<ProvisioningWindowRow> {
  return apiFetch("/api/provisioning/windows", { method: "POST", body });
}

export function closeProvisioningWindow(
  id: number,
): Promise<ProvisioningWindowRow> {
  return apiFetch(`/api/provisioning/windows/${id}/close`, { method: "POST" });
}

export function getAdoptions(): Promise<{
  pending: AdoptionPending[];
  history: AdoptionHistory[];
}> {
  return apiFetch("/api/provisioning/adoptions");
}

export function approveAdoption(
  deviceId: number,
): Promise<Record<string, unknown>> {
  return apiFetch(`/api/provisioning/adoptions/${deviceId}/approve`, {
    method: "POST",
  });
}

export function rejectAdoption(
  deviceId: number,
): Promise<Record<string, unknown>> {
  return apiFetch(`/api/provisioning/adoptions/${deviceId}/reject`, {
    method: "POST",
  });
}

export function getFirmwareImages(): Promise<{ items: FirmwareImageRow[] }> {
  return apiFetch("/api/firmware/images");
}

export interface UploadFirmwareIn {
  vendor: string;
  model: string;
  version: string;
  release_notes?: string;
  file: File;
}

/** Upload a firmware image; the response carries the server-computed SHA-256. */
export function uploadFirmwareImage(
  input: UploadFirmwareIn,
): Promise<FirmwareImageRow> {
  const form = new FormData();
  form.set("vendor", input.vendor);
  form.set("model", input.model);
  form.set("version", input.version);
  if (input.release_notes) form.set("release_notes", input.release_notes);
  form.set("file", input.file);
  return apiFetch("/api/firmware/images", { method: "POST", body: form });
}

export interface CreateRolloutIn {
  image_id: number;
  target_kind: string;
  target_devices?: number[];
  window_start?: string;
  window_end?: string;
}

/** Stage a rollout (test/pilot/fleet or an explicit device set). */
export function createFirmwareRollout(
  body: CreateRolloutIn,
): Promise<FirmwareRolloutRow> {
  return apiFetch("/api/firmware/rollouts", { method: "POST", body });
}

/** The trigger chain's per-device attempt result (ADR 0028). */
export interface ResyncResult {
  device_id: number;
  mac: string;
  classification: "live" | "reboot";
  triggered: boolean;
  deferred: boolean;
  paths: { path: string; outcome: string }[];
}

/** Run the re-provisioning trigger chain now; `force` overrides the
 * after-hours deferral for reboot-class changes (deliberate use only). */
export function resyncDevice(
  deviceId: number,
  force = false,
): Promise<ResyncResult> {
  return apiFetch(`/api/provisioning/devices/${deviceId}/resync`, {
    method: "POST",
    body: { force },
  });
}

/** The operator saw the phone reboot: clear its reboot suggestion (audited). */
export function confirmDeviceRebooted(
  deviceId: number,
): Promise<{ device_id: number; mac: string; reboot_needed_since: null }> {
  return apiFetch(`/api/provisioning/devices/${deviceId}/rebooted`, {
    method: "POST",
  });
}

export function getFirmwareRollouts(): Promise<{
  items: FirmwareRolloutRow[];
}> {
  return apiFetch("/api/firmware/rollouts");
}

/** Activate a staged rollout; `force` waives the after-hours window. */
export function activateRollout(
  id: number,
  force = false,
): Promise<FirmwareRolloutRow> {
  return apiFetch(`/api/firmware/rollouts/${id}/activate`, {
    method: "POST",
    body: { force },
  });
}

export function rollbackRollout(id: number): Promise<FirmwareRolloutRow> {
  return apiFetch(`/api/firmware/rollouts/${id}/rollback`, { method: "POST" });
}

/** One SIP identity bound to a device (ADR 0038). */
export interface DeviceLineRow {
  id: number;
  line_index: number;
  role: "primary" | "backup" | "monitor";
  node_id: number;
  node_name: string;
  domain: string | null;
  extension: string;
  is_e911_line: boolean;
}

/** What the Lines panel needs: existing lines plus node/domain choices. */
export interface DeviceLinesInfo {
  device_id: number;
  mac: string;
  lines: DeviceLineRow[];
  nodes: { id: number; name: string; domains: string[] }[];
  next_line_index: number;
}

export function getDeviceLines(deviceId: number): Promise<DeviceLinesInfo> {
  return apiFetch(`/api/provisioning/devices/${deviceId}/lines`);
}

export interface AssignLineIn {
  extension: string;
  node_id: number;
  domain: string;
  line_index: number;
  role: string;
  is_e911: boolean;
}

/** Bind an extension to a device line: generates + writes the SIP password (ADR 0038). */
export function assignDeviceLine(
  deviceId: number,
  body: AssignLineIn,
): Promise<{
  id: number;
  extension: string;
  line_index: number;
  is_e911_line: boolean;
}> {
  return apiFetch(`/api/provisioning/devices/${deviceId}/lines`, {
    method: "POST",
    body,
  });
}

/** Change a line's role or E911 flag in place (ADR 0038 amendment 2026-10-18). */
export function updateDeviceLine(
  deviceId: number,
  lineId: number,
  body: { role?: string; is_e911?: boolean },
): Promise<{ id: number; device_id: number; role: string; is_e911_line: boolean }> {
  return apiFetch(`/api/provisioning/devices/${deviceId}/lines/${lineId}`, {
    method: "PUT",
    body,
  });
}

/** Unbind a line; the account's SIP password is rotated when no other line shares it. */
export function removeDeviceLine(
  deviceId: number,
  lineId: number,
): Promise<{ removed: boolean; sip_auth_rotated: boolean; shared_with: number }> {
  return apiFetch(`/api/provisioning/devices/${deviceId}/lines/${lineId}`, {
    method: "DELETE",
  });
}

/** Rotate a device's provisioning secret (audited; the secret is never returned). */
export function rotateDeviceSecret(deviceId: number): Promise<{
  device_id: number;
  mac: string;
  rotated: boolean;
  reaches_phone_by_cert: boolean;
  note: string;
}> {
  return apiFetch(`/api/provisioning/devices/${deviceId}/secret/rotate`, {
    method: "POST",
  });
}

/** One phone the importer found provisioned on a node (ADR 0048). */
export interface DiscoveredDeviceRow {
  id: number;
  node_id: number;
  node_name: string | null;
  domain: string;
  mac: string;
  vendor: string | null;
  model: string | null;
  /** Vodia names a phone with a sidecar 'D815-D8C'; the platform splits it (ADR 0048). */
  base_model: string | null;
  expansion_modules: number;
  name: string | null;
  firmware_version: string | null;
  extensions: string[];
  vodia_template: string | null;
  has_buttons: boolean;
  registrations: number;
  registering_ip: string | null;
  /** ADR 0053: the registration Contact's LAN address (the public one above names a NAT'd site). */
  local_ip: string | null;
  /** The node's Location Assignments entry for the phone's extension, by name. */
  vodia_location_name: string | null;
  /** The four signals in precedence order, each with the location it named (or null). */
  location_signals: {
    signal: "vodia_assignment" | "carrier_eani" | "lan_ip" | "public_ip";
    value: string | null;
    location_id: number | null;
    location_name: string | null;
  }[];
  /** Two resolved signals named different locations. */
  location_conflict: boolean;
  /** Proposed from the highest-precedence signal that resolved (ADR 0053). */
  proposed_location_id: number | null;
  proposed_location_name: string | null;
  device_id: number | null;
  state: "new" | "pre_registered" | "ignored";
  /** Whether the platform has a provisioning profile for this model (§8). */
  profile_known: boolean;
  last_seen: string | null;
}

export function getDiscoveredDevices(): Promise<{
  items: DiscoveredDeviceRow[];
  total: number;
  counts: Record<string, number>;
}> {
  return apiFetch("/api/sync/devices/discovered");
}

/** Pre-register discovered phones; a confirmed location attests them (ADR 0047). */
export function preRegisterDiscovered(
  items: { id: number; location_id?: number | null }[],
  attest = true,
): Promise<{
  items: {
    mac: string;
    device_id: number;
    created: boolean;
    location_id: number | null;
    placed: boolean;
    attested: boolean;
    profile_known: boolean;
    extensions: string[];
  }[];
  count: number;
}> {
  return apiFetch("/api/sync/devices/discovered/pre-register", {
    method: "POST",
    body: JSON.stringify({ items, attest }),
  });
}

export function setDiscoveredState(
  ids: number[],
  state: "ignored" | "new",
): Promise<{ changed: number }> {
  return apiFetch("/api/sync/devices/discovered/state", {
    method: "POST",
    body: JSON.stringify({ ids, state }),
  });
}

/** Bulk pre-registration report (ADR 0050): dry-run or commit. */
export interface DeviceImportReport {
  dry_run?: boolean;
  parsed_rows?: number;
  would_create?: number;
  would_bind_lines?: number;
  would_place?: number;
  valid?: boolean;
  created?: number;
  lines_bound?: number;
  attested?: number;
  skipped?: number;
  errors: { line: number; field: string; message: string }[];
}

export function importDevicesCsv(
  csv: string,
  dryRun: boolean,
  attest = true,
): Promise<DeviceImportReport> {
  return apiFetch("/api/provisioning/devices/import", {
    method: "POST",
    body: JSON.stringify({ csv, dry_run: dryRun, attest }),
  });
}

/** URL for the device CSV export (same-origin GET the cookie authorizes). */
export function devicesExportUrl(): string {
  return "/api/provisioning/devices/export.csv";
}
